import imageCompression from "browser-image-compression";
import { PDFDocument } from "pdf-lib";

const MAX_SIZE_MB = 1;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

/**
 * Compress an image using browser-image-compression.
 * Falls back to the original file if compression fails.
 */
export async function compressImage(file: File): Promise<File> {
    if (file.size <= MAX_SIZE_BYTES) return file;

    try {
        const compressed = await imageCompression(file, {
            maxSizeMB: MAX_SIZE_MB,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
        });

        // Preserve the original filename
        return new File([compressed], file.name, { type: compressed.type || file.type });
    } catch (err) {
        console.error(`Image compression failed for "${file.name}":`, err);
        return file;
    }
}

/**
 * Compress a PDF using pdf-lib's object stream compression.
 * Returns null for non-PDF files over 1MB so the caller can show a toast.
 * Returns the original file if it's already under 1MB.
 */
export async function compressDocument(file: File): Promise<File | null> {
    if (file.size <= MAX_SIZE_BYTES) return file;

    if (file.type !== "application/pdf") {
        // Signal to the caller that this file type can't be compressed
        return null;
    }

    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
        const compressedBytes = await pdfDoc.save({ useObjectStreams: true });
        const blob = new Blob([compressedBytes.buffer.slice(0) as ArrayBuffer], { type: "application/pdf" });
        const compressedFile = new File([blob], file.name, { type: "application/pdf" });

        if (compressedFile.size > MAX_SIZE_BYTES) {
            console.warn(`"${file.name}" is still over 1MB after PDF compression (${(compressedFile.size / 1024 / 1024).toFixed(2)} MB).`);
        }

        return compressedFile;
    } catch (err) {
        console.error(`PDF compression failed for "${file.name}":`, err);
        return file;
    }
}